// arsenal-tpms/frontend/src/components/vehicles/detail/VehicleSchematic.jsx
import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Tooltip,
    useTheme,
    alpha,
    useMediaQuery
} from '@mui/material';
import {
    Sensors as SensorIcon,
    CheckCircle as CheckCircleIcon,
    TouchApp as TouchIcon
} from '@mui/icons-material';

const ensureString = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    return fallback;
};

const VehicleSchematic = ({
    vehicle,
    t,
    onPositionClick,
    editMode = false,
    rotationMode = false,
    selectedPositions = [],
    compact = false
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!vehicle || !vehicle.positions_count) return null;

    const createLayout = () => {
        if (!vehicle.figure) {
            return [Array.from({ length: vehicle.positions_count }, (_, i) => i + 1)];
        }

        const axleDistribution = vehicle.figure.split(',').map(a => parseInt(a, 10)).filter(num => !isNaN(num));
        const figureTotal = axleDistribution.reduce((sum, positions) => sum + positions, 0);

        let layout = [];
        let positionCounter = 1;

        if (figureTotal === vehicle.positions_count) {
            axleDistribution.forEach(positionsPerAxle => {
                const row = [];
                for (let i = 0; i < positionsPerAxle; i++) {
                    row.push(positionCounter++);
                }
                layout.push(row);
            });
        } else {
            const scaleFactor = vehicle.positions_count / figureTotal;
            let remainingPositions = vehicle.positions_count;

            for (let axleIndex = 0; axleIndex < axleDistribution.length; axleIndex++) {
                if (axleIndex === axleDistribution.length - 1) {
                    const row = [];
                    for (let i = 0; i < remainingPositions; i++) {
                        row.push(positionCounter++);
                    }
                    if (row.length > 0) layout.push(row);
                    break;
                }

                const originalPositions = axleDistribution[axleIndex];
                const scaledPositions = Math.round(originalPositions * scaleFactor);
                const positionsToUse = Math.min(scaledPositions, remainingPositions);

                const row = [];
                for (let i = 0; i < positionsToUse; i++) {
                    row.push(positionCounter++);
                }

                if (row.length > 0) {
                    layout.push(row);
                    remainingPositions -= row.length;
                }

                if (remainingPositions <= 0) break;
            }
        }

        return layout;
    };

    const layout = createLayout();
    const boxSize = compact ? (isMobile ? 45 : 55) : (isMobile ? 50 : 60);

    return (
        <Box sx={{
            border: 2,
            borderColor: 'primary.main',
            borderRadius: { xs: 2, sm: 3 },
            p: { xs: 2, sm: 3, md: 4 },
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[2],
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 2, sm: 3 },
            maxWidth: '100%',
            mx: 'auto'
        }}>
            {!compact && (
                <Typography
                    variant={isMobile ? "subtitle1" : "h6"}
                    fontWeight={700}
                    color="primary.main"
                    gutterBottom
                    textAlign="center"
                >
                    {t('vehicleDetail.overview.schematic', 'Esquemático del Vehículo')}
                </Typography>
            )}

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2 },
                position: 'relative',
                zIndex: 1
            }}>
                {layout.map((row, rowIndex) => (
                    <Box
                        key={`row-${rowIndex}`}
                        sx={{
                            display: 'flex',
                            gap: { xs: 1.5, sm: 2 },
                            justifyContent: row.length === 2 ? 'space-between' : 'center',
                            minWidth: row.length === 2 ? (isMobile ? '140px' : '180px') : 'auto',
                            alignItems: 'center'
                        }}
                    >
                        {row.map(position => {
                            const hasSensor = vehicle.vehicle_sensors?.some(vs => vs.position === position);
                            const assignedSensor = vehicle.vehicle_sensors?.find(vs => vs.position === position);
                            const isSelected = selectedPositions.includes(position);
                            const canInteract = (editMode && !hasSensor) || (rotationMode && hasSensor);

                            return (
                                <Tooltip
                                    key={`pos-${position}`}
                                    title={hasSensor ? ensureString(assignedSensor?.sensor_id, 'Sensor') : ''}
                                    arrow
                                    placement="top"
                                >
                                    <Box
                                        onClick={() => canInteract && onPositionClick && onPositionClick(position)}
                                        sx={{
                                            width: boxSize,
                                            height: boxSize,
                                            border: '3px solid',
                                            borderColor: isSelected ? 'primary.main' :
                                                hasSensor ? 'success.main' : 'grey.400',
                                            borderRadius: 2,
                                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.25) :
                                                hasSensor ? alpha(theme.palette.success.main, 0.15) :
                                                    alpha(theme.palette.grey[300], 0.3),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            fontWeight: 700,
                                            color: isSelected ? 'primary.dark' :
                                                hasSensor ? 'success.dark' : 'grey.700',
                                            transition: 'all 0.3s ease',
                                            cursor: canInteract ? 'pointer' : 'default',
                                            position: 'relative',
                                            boxShadow: isSelected ? theme.shadows[4] :
                                                hasSensor ? theme.shadows[3] : theme.shadows[1],
                                            '&:hover': canInteract ? {
                                                transform: 'translateY(-2px) scale(1.05)',
                                                boxShadow: theme.shadows[6],
                                                borderColor: rotationMode ? 'primary.main' :
                                                    editMode ? 'primary.main' : undefined,
                                                bgcolor: rotationMode ? alpha(theme.palette.primary.main, 0.25) :
                                                    editMode ? alpha(theme.palette.primary.main, 0.15) : undefined,
                                            } : {}
                                        }}
                                    >
                                        <Typography variant={isMobile ? "body2" : "body1"} fontWeight={700}>
                                            {position}
                                        </Typography>

                                        {hasSensor && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: -2,
                                                    right: -2,
                                                    width: { xs: 16, sm: 18 },
                                                    height: { xs: 16, sm: 18 },
                                                    borderRadius: '50%',
                                                    bgcolor: isSelected ? 'primary.main' : 'success.main',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: theme.shadows[2]
                                                }}
                                            >
                                                {isSelected ? (
                                                    <CheckCircleIcon sx={{ fontSize: { xs: 10, sm: 12 }, color: 'white' }} />
                                                ) : (
                                                    <SensorIcon sx={{ fontSize: { xs: 10, sm: 12 }, color: 'white' }} />
                                                )}
                                            </Box>
                                        )}

                                        {rotationMode && hasSensor && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: -2,
                                                    left: -2,
                                                    width: { xs: 16, sm: 18 },
                                                    height: { xs: 16, sm: 18 },
                                                    borderRadius: '50%',
                                                    bgcolor: alpha(theme.palette.primary.main, 0.9),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: theme.shadows[2]
                                                }}
                                            >
                                                <TouchIcon sx={{ fontSize: { xs: 10, sm: 12 }, color: 'white' }} />
                                            </Box>
                                        )}
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>
                ))}
            </Box>

            {!compact && (
                <>
                    <Box sx={{
                        mt: { xs: 2, sm: 3 },
                        display: 'flex',
                        gap: { xs: 2, sm: 4 },
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        p: { xs: 1.5, sm: 2 },
                        bgcolor: alpha(theme.palette.grey[100], 0.5),
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        width: '100%'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                                width: { xs: 20, sm: 24 },
                                height: { xs: 20, sm: 24 },
                                border: '3px solid',
                                borderColor: 'success.main',
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.success.main, 0.15),
                                boxShadow: theme.shadows[1]
                            }} />
                            <Typography variant={isMobile ? "caption" : "body2"} color="success.dark" fontWeight={600}>
                                {t('vehicleDetail.overview.withSensor', 'Con Sensor')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                                width: { xs: 20, sm: 24 },
                                height: { xs: 20, sm: 24 },
                                border: '3px solid',
                                borderColor: 'grey.400',
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.grey[300], 0.3),
                                boxShadow: theme.shadows[1]
                            }} />
                            <Typography variant={isMobile ? "caption" : "body2"} color="grey.700" fontWeight={600}>
                                {t('vehicleDetail.overview.withoutSensor', 'Sin Sensor')}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{
                        mt: 1,
                        display: 'flex',
                        gap: { xs: 1.5, sm: 3 },
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <Chip
                            label={`${vehicle.vehicle_sensors?.length || 0} ${t('vehicleDetail.overview.sensorsAssigned', 'Sensores')}`}
                            color="success"
                            variant="outlined"
                            size={isMobile ? "small" : "medium"}
                        />
                        <Chip
                            label={`${vehicle.positions_count - (vehicle.vehicle_sensors?.length || 0)} ${t('vehicleDetail.overview.freePositions', 'Libres')}`}
                            color="default"
                            variant="outlined"
                            size={isMobile ? "small" : "medium"}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default VehicleSchematic;